import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Radio, Scissors, Swords, Users, BarChart3, Tag, Languages, Sun, Moon, LogOut, Zap } from "lucide-react";
import { useLang } from "@/i18n";
import { useTheme } from "@/theme";
import { useAuth } from "@/context/AuthContext";

export function Brand({ onClick }) {
  return (
    <div className="flex items-center gap-2 cursor-pointer select-none" onClick={onClick} data-testid="header-logo">
      <div className="relative flex items-center justify-center h-8 w-8 rounded-lg bg-[#E5253A]">
        <Radio className="h-4 w-4 text-white" />
        <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 live-dot" />
      </div>
      <div className="leading-none">
        <div className="font-extrabold tracking-tight text-sm">YES GO <span className="text-[#E5253A]">LIVE</span></div>
        <div className="text-[9px] font-mono text-muted-foreground tracking-widest">AI · BROADCAST</div>
      </div>
    </div>
  );
}

export default function AppLayout({ children, active }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, toggle: toggleLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const { user, logout } = useAuth();

  const nav = [
    { key: "command", label: t("nav_command"), icon: LayoutDashboard, path: "/dashboard" },
    { key: "studio", label: t("nav_studio"), icon: Radio, path: "/studio" },
    { key: "clips", label: t("nav_clips"), icon: Scissors, path: "/clips" },
    { key: "battle", label: t("nav_battle"), icon: Swords, path: "/battle" },
    { key: "leads", label: t("nav_leads"), icon: Users, path: "/leads" },
    { key: "analytics", label: t("nav_analytics"), icon: BarChart3, path: "/analytics" },
    { key: "pricing", label: t("nav_pricing"), icon: Tag, path: "/pricing" },
  ];

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-border bg-card/40 shrink-0">
        <div className="px-5 py-4 border-b border-border">
          <Brand onClick={() => navigate("/dashboard")} />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => {
            const isActive = active === n.key;
            return (
              <button
                key={n.key}
                data-testid={`nav-${n.key}-tab`}
                onClick={() => navigate(n.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#E5253A]/15 text-[#E5253A]" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <button
            data-testid="go-live-btn"
            onClick={() => navigate("/studio")}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white text-sm font-bold transition-colors"
          >
            <Zap className="h-4 w-4" /> {t("go_live")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="md:hidden"><Brand onClick={() => navigate("/dashboard")} /></div>
          <div className="hidden md:block text-sm font-semibold tracking-tight capitalize">
            {nav.find((n) => n.key === active)?.label || "Yes Go Live"}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="language-toggle-btn" onClick={toggleLang}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border hover:bg-accent text-xs font-semibold font-mono uppercase transition-colors">
              <Languages className="h-4 w-4" /> {lang}
            </button>
            <button data-testid="theme-toggle-btn" onClick={toggleTheme}
              className="flex items-center justify-center h-9 w-9 rounded-lg border border-border hover:bg-accent transition-colors">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user && (
              <div className="flex items-center gap-2 pl-1">
                <img src={user.picture || "https://via.placeholder.com/64"} alt="" className="h-8 w-8 rounded-full object-cover border border-border" />
                <div className="hidden sm:block leading-tight">
                  <div className="text-xs font-semibold max-w-[120px] truncate">{user.name}</div>
                  <div className="text-[10px] font-mono text-emerald-400">{user.plan} · {user.ai_credits} cr</div>
                </div>
                <button data-testid="logout-btn" onClick={logout} className="flex items-center justify-center h-9 w-9 rounded-lg border border-border hover:bg-accent transition-colors">
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Mobile nav */}
        <div className="md:hidden flex items-center gap-1 overflow-x-auto px-3 py-2 border-b border-border">
          {nav.map((n) => (
            <button key={n.key} data-testid={`mnav-${n.key}-tab`} onClick={() => navigate(n.path)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${active === n.key ? "bg-[#E5253A] text-white" : "bg-accent text-muted-foreground"}`}>
              {n.label}
            </button>
          ))}
        </div>

        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
